import { APIClient } from 'infra/APIClient'
import { Observable } from 'rxjs'
import { retry, share, switchMap } from 'rxjs/operators'
import { APIConfig, CursorPaged } from 'types/definitions'
import { rethrowRequestError } from 'utils/error-util'
import { MessageBankDTO, MessageBankReplyDTO } from './dtos/message-bank'
import {
  ConversationDTO,
  ConversationUnreadCountResponseDTO,
  MessageDTO,
  MessageEventsDTO,
  MessageQuery,
  ModifyParticipantPayload,
  OnlineStatusDTO,
  ParticipantDTO,
} from './dtos/messages'

export interface MessagingAPI {
  events$: Observable<MessageEventsDTO>
  conversations: ConversationAPI
}

export function createMessagingAPI(opts: APIConfig): MessagingAPI {
  return {
    conversations: new ConversationAPI(opts),
    events$: opts.token$.pipe(
      switchMap(
        (token) =>
          new Observable<MessageEventsDTO>((observer) => {
            if (!token) {
              return
            }

            const eventSource = new EventSource(
              `${opts.baseURL!}/?__authorization=${token}`,
            )
            eventSource.onmessage = (message) => {
              const data = JSON.parse(message.data) as MessageEventsDTO
              observer.next(data)
            }
            eventSource.onerror = (error) => {
              console.error('Error in EventSource', error)
              observer.error(error)
            }
            return () => {
              eventSource.close()
            }
          }),
      ),
      retry({ delay: 1000 }),
      share(),
    ),
  }
}

export function createMessageBankAPI(opts: APIConfig): MessageBankAPI {
  return new MessageBankAPI(opts)
}

export class ConversationAPI extends APIClient {
  getConversationById(id: string): Promise<ConversationDTO> {
    return this.client
      .get(`/v3/conversations/${id}`)
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  createMessage(conversationId: string, message: string): Promise<MessageDTO> {
    return this.client
      .post(`/v3/conversations/${conversationId}/messages/text`, {
        text: message,
      })
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  createAdminMessage(
    conversationId: string,
    message: string,
  ): Promise<MessageDTO> {
    return this.client
      .post(`/v3/conversations/${conversationId}/messages/admin`, {
        text: message,
      })
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  createNote(conversationId: string, message: string): Promise<MessageDTO> {
    return this.client
      .post(`/v3/conversations/${conversationId}/messages/note`, {
        text: message,
      })
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  createAdminNote(
    conversationId: string,
    message: string,
  ): Promise<MessageDTO> {
    return this.client
      .post(`/v3/conversations/${conversationId}/messages/adminNote`, {
        text: message,
      })
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  createTeamNote(
    conversationId: string,
    teamId: string,
    message: string,
  ): Promise<MessageDTO> {
    return this.client
      .post(`/v3/conversations/${conversationId}/messages/teamNote`, {
        text: message,
        team_id: teamId,
      })
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  getMessages(
    id: string,
    params: MessageQuery,
  ): Promise<CursorPaged<MessageDTO>> {
    return this.client
      .get(`/v3/conversations/${id}/messages`, {
        params,
      })
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  getHumanMessages(id: string, workspaceId: string): Promise<MessageDTO[]> {
    return this.client
      .get(`/v3/conversations/${id}/${workspaceId}/humanMessages`)
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  isOnline(userPublicIds: string[]): Promise<OnlineStatusDTO[]> {
    return this.client
      .post(`/v3/users/status/id-query`, userPublicIds)
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }

  markRead(workspaceId: string, messageIds: Array<string>): Promise<void> {
    return this.client
      .post('/v3/messages/read', {
        workspace_id: workspaceId,
        message_ids: messageIds,
      })
      .then(() => undefined)
      .catch(rethrowRequestError)
  }

  getUnreadCounts(): Promise<ConversationUnreadCountResponseDTO> {
    return this.client
      .get('/v3/messages/unread')
      .then(this._data)
      .catch(rethrowRequestError)
  }

  updateParticipant(params: ModifyParticipantPayload): Promise<ParticipantDTO> {
    return this.client
      .patch(
        `/v3/conversations/${params.conversation_id}/participants/${params.user_id}/update`,
        params,
      )
      .then((response) => response.data)
      .catch(rethrowRequestError)
  }
}

export class MessageBankAPI extends APIClient {
  async findForWorkspace(workspaceId: string): Promise<MessageBankDTO> {
    return this.client
      .get(`/v3/message-banks/find`, {
        params: { workspace_id: workspaceId },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async getReplies(
    messageBankId: string,
  ): Promise<CursorPaged<MessageBankReplyDTO>> {
    return this.client
      .get(`/v3/message-bank-replies`, {
        params: { message_bank_id: messageBankId, order_by: 'title asc' },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }
}
